/**
 * O que o chat guarda no browser: rascunhos por conversa e mensagens por enviar (sem ligação).
 *
 * São conveniências de quem está ao computador; a base de dados é a fonte de verdade. O armazenamento pode não existir
 * (janela privada, dados limpos): cada leitura e escrita aguenta isso e a app continua a funcionar sem ele.
 */

// Por pessoa: num computador partilhado, quem entra a seguir não herda os rascunhos nem a fila de quem saiu.
let owner = "anonimo";
const draftPrefix = () => `chat:${owner}:draft:`;
const outboxKey = () => `chat:${owner}:outbox`;

export function setStorageOwner(userId: string | null): void {
  owner = userId ?? "anonimo";
}
const MAX_DRAFT_CHARS = 8000;
const MAX_OUTBOX = 50;

export interface PendingMessage {
  conversationId: string;
  clientKey: string;
  content: string;
  threadRootId: string | null;
  /** A mensagem a que esta responde (citação). */
  replyToId?: string | null;
  createdAt: string;
  /** Os anexos e as menções vão no reenvio («Tentar de novo» ou depois de recarregar), não só o texto. */
  attachments?: unknown[];
  mentions?: unknown[];
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readDraft(conversationId: string, threadRootId: string | null = null): string {
  try {
    return storage()?.getItem(`${draftPrefix()}${conversationId}:${threadRootId ?? ""}`) ?? "";
  } catch {
    return "";
  }
}

export function writeDraft(conversationId: string, text: string, threadRootId: string | null = null): void {
  const key = `${draftPrefix()}${conversationId}:${threadRootId ?? ""}`;
  try {
    if (text.trim()) storage()?.setItem(key, text.slice(0, MAX_DRAFT_CHARS));
    else storage()?.removeItem(key);
  } catch {
    // sem armazenamento: o rascunho fica só na caixa de texto
  }
}

export function readOutbox(): PendingMessage[] {
  try {
    const raw = storage()?.getItem(outboxKey());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isPending) : [];
  } catch {
    return [];
  }
}

function isPending(value: unknown): value is PendingMessage {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.conversationId === "string" && typeof v.clientKey === "string" && typeof v.content === "string";
}

function saveOutbox(items: PendingMessage[]): void {
  try {
    storage()?.setItem(outboxKey(), JSON.stringify(items.slice(-MAX_OUTBOX)));
  } catch {
    // sem armazenamento: a mensagem continua na lista em memória, com «tentar de novo»
  }
}

export function enqueuePending(message: PendingMessage): void {
  saveOutbox([...readOutbox().filter((m) => m.clientKey !== message.clientKey), message]);
}

export function removePending(clientKey: string): void {
  saveOutbox(readOutbox().filter((m) => m.clientKey !== clientKey));
}

/** Chave do cliente por mensagem: o servidor nunca grava duas vezes a mesma (reenvio idempotente). */
export function newClientKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
